import { keyframes } from '@emotion/react';
import { Styles, css, extractFont, withStyles } from '@recruit-robin/robin-theme/web-styles';
import { generateClassname } from 'helpers/helpers';
import { generateTransitionStyles } from 'styles/theme';
import { ThumbStyleStates } from './types';

export const thumbHoveringParent = generateClassname();

const tooltipOpen = keyframes`
  0% {
    visibility: hidden;
  }

  100% {
    visibility: visible;
  }
`;

const tooltipClose = keyframes`
  0% {
    visibility: visible;
  }

  100% {
    visibility: hidden;
  }
`;

const tooltipVisibleStyles = css({
  transform: 'translateY(-3px)',
  opacity: 1,
  animation: `${tooltipOpen} 120ms cubic-bezier(.22,.9,.53,1) forwards`,
});

const styles = {
  root: css({
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    top: '-12px',
  }),
  thumb: ({ theme }) =>
    css({
      '&.MuiSlider-thumb': {
        position: 'absolute',
        color: theme.colors.neutral.variant[60],
        width: '16px',
        height: '16px',
        marginTop: 'unset',
        marginLeft: 'unset',
        boxShadow: 'unset',
        outline: 'unset',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'grab',
        boxSizing: 'border-box',
        border: `2px solid currentColor`,
        backgroundColor: 'currentColor',
        transition: generateTransitionStyles(['color', 'background-color', 'transform'], '120ms'),
        [`.${thumbHoveringParent}:not(.m-range-focused):hover &`]: {
          color: theme.colors.neutral.variant[70],
          backgroundColor: theme.colors.neutral.variant[70],
        },
        '&.MuiSlider-active, &.MuiSlider-active:hover, &.Mui-focusVisible, &.Mui-focusVisible:hover, &:hover': {
          boxShadow: 'unset',
          outline: 'unset',
          transform: 'scale(1.25)',
          '& + .thumb--tooltip': {
            ...tooltipVisibleStyles,
          },
          '& + span.thumb--tooltip': {
            transform: 'translateY(-5px)',
          },
        },
        '.m-range-focused &': {
          color: theme.colors.primary.fuchsia[50],
        },
        '&.MuiSlider-active, &.MuiSlider-active:hover, &.Mui-focusVisible, &.Mui-focusVisible:hover': {
          color: theme.colors.primary.fuchsia[50],
          backgroundColor: theme.colors.primary.fuchsia[50],
        },
        '&.Mui-disabled': {
          width: '16px',
          height: '16px',
          marginTop: 'unset',
          marginLeft: 'unset',
          color: theme.colors.neutral.variant[30],
        },
        '&::after': {
          display: 'none',
        },
        '& > .thumb--active-background': {
          position: 'absolute',
          display: 'block',
          width: '10px',
          height: '10px',
          top: '1px',
          left: '1px',
          backgroundColor: 'currentColor',
          borderRadius: '50%',
        },
      },
    }),
  tooltip: ({ theme }) =>
    css({
      color: theme.colors.neutral.variant[70],
      position: 'absolute',
      width: '28.8px',
      height: '28.8px',
      borderRadius: '50%',
      backgroundColor: 'currentColor',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      top: '-42px',
      opacity: 0,
      visibility: 'hidden',
      transition: generateTransitionStyles(['color', 'background-color', 'transform', 'opacity'], '120ms'),
      animation: `${tooltipClose} 120ms cubic-bezier(.22,.9,.53,1) forwards`,
      '& > .thumb--tooltip-label': {
        ...extractFont((font) => font.supporting['label-tiny'], 'web'),
        color: theme.colors.neutral.variant[0],
      },
      '& > svg': {
        position: 'absolute',
        width: '100%',
        top: '50%',
        zIndex: -1,
      },
      '.Mui-focusVisible + &, .MuiSlider-active + &, .m-range-focused &': {
        color: theme.colors.primary.fuchsia[50],
      },
    }),
} satisfies Styles<ThumbStyleStates>;

export const useStyles = withStyles(styles);
